import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsappIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
);

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Por favor, rellena tu nombre y email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Gracias! Te escribiremos muy pronto. 🌱");
      setForm({ name: "", email: "" });
    } catch (err) {
      toast.error("Algo salió mal. Prueba por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-form-section"
      className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-32"
    >
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-green mb-5">
            Hablemos
          </p>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.9]">
            Cuéntanos tu <span className="text-green">idea</span>.
          </h2>
          <p className="mt-6 text-ink/60 text-base md:text-lg max-w-md leading-relaxed">
            Déjanos tu nombre y email y te contactamos, o escríbenos directamente
            por WhatsApp. Sin compromiso, con mucha frescura.
          </p>

          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-green text-ink font-bold text-base px-7 py-4 hover:brightness-95 transition-all"
          >
            <WhatsappIcon size={22} />
            Escríbenos
          </a>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-forest text-cream p-8 md:p-12 relative overflow-hidden"
        >
          <div className="noise-overlay absolute inset-0 opacity-10 pointer-events-none" />
          <div className="relative space-y-6">
            <div>
              <label htmlFor="cf-name" className="block text-sm font-semibold mb-2 text-cream/80">
                Tu nombre
              </label>
              <input
                id="cf-name"
                data-testid="contact-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. María García"
                className="w-full rounded-2xl bg-cream/10 border border-cream/20 px-5 py-4 text-cream placeholder:text-cream/40 outline-none focus:border-mango focus:bg-cream/15 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="cf-email" className="block text-sm font-semibold mb-2 text-cream/80">
                Tu email
              </label>
              <input
                id="cf-email"
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="hola@tumarca.com"
                className="w-full rounded-2xl bg-cream/10 border border-cream/20 px-5 py-4 text-cream placeholder:text-cream/40 outline-none focus:border-mango focus:bg-cream/15 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-mango hover:bg-mango-hover text-ink font-bold text-base px-7 py-4 transition-colors disabled:opacity-70"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Enviando..." : "Enviar mensaje"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
